<template>
  <div class="songkran-ranking">
    <!-- Hero Header -->
    <div class="ranking-hero">
      <div class="hero-bg"></div>
      <div class="hero-content">
        <div class="hero-trophy">🏆</div>
        <h1 class="hero-title">SONGKRAN<br>RANKING</h1>
        <div class="hero-date"><span class="date-badge">6-10 เมษายน 2026</span></div>
        <div class="prize-row">
          <div class="prize-card prize-gold">
            <div class="prize-icon">🥇</div>
            <div class="prize-label">อันดับ 1-5</div>
            <div class="prize-amount">100 <small>เหรียญ</small></div>
          </div>
          <div class="prize-card prize-silver">
            <div class="prize-icon">🥈</div>
            <div class="prize-label">อันดับ 6-10</div>
            <div class="prize-amount">50 <small>เหรียญ</small></div>
          </div>
        </div>
        <!-- Play button -->
        <button v-if="isPlayDay" class="btn-play-hero" @click="goPlay">🎮 เข้าเล่นเกม</button>
        <div v-if="isClaimDay" class="claim-date-note">รับรางวัลได้วันที่ 11-12 เมษายน</div>
      </div>
    </div>

    <!-- User info -->
    <div v-if="pictureUrl" class="my-info">
      <img :src="pictureUrl" class="my-avatar" />
      <span class="my-name">{{ displayName }}</span>
      <span v-if="myRank > 0" class="my-rank-badge">อันดับ {{ myRank }}</span>
    </div>

    <!-- Claim reward banner -->
    <div v-if="canClaim && !claimed" class="claim-banner">
      <div class="claim-inner">
        <div class="claim-text">
          <div class="claim-title">ยินดีด้วย! 🎉</div>
          <div class="claim-desc">คุณอยู่อันดับ <strong>{{ myRank }}</strong> ได้รับ <strong>{{ myReward }}</strong> เหรียญ</div>
        </div>
        <button class="btn-claim" @click="claimReward" :disabled="claiming">{{ claiming ? 'กำลังรับ...' : 'รับรางวัล' }}</button>
      </div>
    </div>
    <div v-if="claimed" class="claim-banner claimed-banner">
      <div class="claim-inner">
        <div class="claim-text">
          <div class="claim-title">รับรางวัลแล้ว ✅</div>
          <div class="claim-desc">ได้รับ {{ myReward }} เหรียญเรียบร้อย</div>
        </div>
      </div>
    </div>

    <!-- Ranking panel -->
    <div class="ranking-panel">
      <div class="panel-header">
        <span class="panel-title">🏅 กระดานคะแนน</span>
        <span class="panel-count" v-if="rankings.length">{{ rankings.length }} คน</span>
      </div>

      <div v-if="loading" class="ranking-loading"><div class="loading-spinner"></div><span>กำลังโหลด...</span></div>
      <div v-else-if="rankings.length === 0" class="ranking-empty">
        <div class="empty-icon">🎯</div>
        <div class="empty-text">ยังไม่มีผู้เข้าร่วม</div>
        <div class="empty-sub">เล่นเกมจับคู่เพื่อขึ้นกระดาน!</div>
      </div>

      <div v-else class="ranking-body">
        <!-- Podium top 3 -->
        <div v-if="rankings.length >= 3" class="podium">
          <div class="podium-item podium-2" v-if="rankings[1]">
            <div class="podium-medal">🥈</div>
            <div class="podium-code">{{ rankings[1].code }}</div>
            <div class="podium-score">{{ rankings[1].totalScore.toLocaleString() }}</div>
            <div class="podium-bar bar-2"></div>
          </div>
          <div class="podium-item podium-1" v-if="rankings[0]">
            <div class="podium-crown">👑</div>
            <div class="podium-medal">🥇</div>
            <div class="podium-code">{{ rankings[0].code }}</div>
            <div class="podium-score">{{ rankings[0].totalScore.toLocaleString() }}</div>
            <div class="podium-bar bar-1"></div>
          </div>
          <div class="podium-item podium-3" v-if="rankings[2]">
            <div class="podium-medal">🥉</div>
            <div class="podium-code">{{ rankings[2].code }}</div>
            <div class="podium-score">{{ rankings[2].totalScore.toLocaleString() }}</div>
            <div class="podium-bar bar-3"></div>
          </div>
        </div>

        <!-- 4th onwards -->
        <div
          v-for="(item, index) in rankings.slice(rankings.length >= 3 ? 3 : 0)"
          :key="item.id"
          class="ranking-item"
          :class="{ 'rank-top5': getRank(index) <= 5, 'rank-top10': getRank(index) > 5 && getRank(index) <= 10, 'rank-normal': getRank(index) > 10, 'rank-me': item.userkey === userKey }"
        >
          <div class="rank-number" :class="{'num-top5': getRank(index) <= 5, 'num-top10': getRank(index) > 5 && getRank(index) <= 10}">{{ getRank(index) }}</div>
          <div class="rank-info">
            <div class="rank-code">{{ item.code }}</div>
            <div class="rank-detail">{{ item.daysPlayed }} วัน · {{ item.totalMoves }} ครั้ง · {{ item.totalTimeUsed }}วิ</div>
          </div>
          <div class="rank-score" :class="{'score-top5': getRank(index) <= 5, 'score-top10': getRank(index) > 5 && getRank(index) <= 10}">{{ item.totalScore.toLocaleString() }}</div>
          <div class="rank-reward" v-if="getRank(index) <= 10">
            <span v-if="getRank(index) <= 5" class="reward-gold">🪙100</span>
            <span v-else class="reward-silver">🪙50</span>
          </div>
        </div>
      </div>
    </div>

    <div class="ranking-footer">
      <button class="btn-back" @click="closeOrBack">← กลับ</button>
    </div>
  </div>
</template>

<script>
import firebase from 'firebase/app'
import 'firebase/firestore'
import 'firebase/database'
import 'firebase/functions'
import router from './../router'
import liffService from '@/services/liff'
import moment from 'moment-timezone'

export default {
  name: 'SongkranRanking',
  data () {
    return {
      userKey: '',
      displayName: '',
      pictureUrl: '',
      loading: true,
      rankings: [],
      myRank: 0,
      myReward: 0,
      claimed: false,
      claiming: false
    }
  },
  computed: {
    isPlayDay () {
      const today = moment().tz('Asia/Bangkok')
      const d = today.date(), m = today.month()
      return m === 3 && d >= 6 && d <= 10
    },
    isClaimDay () {
      const today = moment().tz('Asia/Bangkok').format('MM-DD')
      return today === '04-11' || today === '04-12'
    },
    canClaim () {
      return this.isClaimDay && this.myRank > 0 && this.myRank <= 10 && !this.claimed
    }
  },
  mounted () {
    this.loadUserAndRanking()
  },
  methods: {
    getRank (index) {
      return index + (this.rankings.length >= 3 ? 4 : 1)
    },

    async loadUserAndRanking () {
      const profile = liffService.profile
      if (profile) {
        this.displayName = profile.displayName || ''
        this.pictureUrl = profile.pictureUrl || ''
        const userSnap = await firebase.database().ref('/users').orderByChild('userId').equalTo(profile.userId).once('value')
        const userData = userSnap.val()
        if (userData) {
          this.userKey = Object.keys(userData)[0]
        }
      }
      await this.loadRanking()
    },

    async loadRanking () {
      this.loading = true
      const snapshot = await firebase.firestore().collection('songkran_memory_total')
        .orderBy('totalScore', 'desc')
        .limit(100)
        .get()

      this.rankings = []
      snapshot.forEach((doc) => { this.rankings.push({ id: doc.id, ...doc.data() }) })

      this.rankings.sort((a, b) => {
        if (b.totalScore !== a.totalScore) return b.totalScore - a.totalScore
        if (a.totalMoves !== b.totalMoves) return a.totalMoves - b.totalMoves
        return a.totalTimeUsed - b.totalTimeUsed
      })

      this.rankings.forEach((item, index) => {
        if (item.userkey === this.userKey) {
          this.myRank = index + 1
          this.claimed = item.claimed === true
          if (this.myRank <= 5) this.myReward = 100
          else if (this.myRank <= 10) this.myReward = 50
        }
      })
      this.loading = false
    },

    async claimReward () {
      if (this.claiming || this.claimed) return
      this.claiming = true
      try {
        const claimFn = firebase.app().functions('asia-east2').httpsCallable('claimSongkranReward')
        const result = await claimFn({ userkey: this.userKey, code: '' })
        if (result.data.success) {
          this.claimed = true
          alert(`รับรางวัลสำเร็จ! ได้รับ ${result.data.reward} เหรียญ (อันดับ ${result.data.rank})`)
        }
      } catch (err) {
        let msg = 'เกิดข้อผิดพลาด'
        if (err.code === 'already-exists') msg = 'คุณรับรางวัลไปแล้ว'
        else if (err.code === 'not-found') msg = 'คุณไม่ได้อยู่ใน Top 10'
        else if (err.code === 'failed-precondition') msg = 'ยังไม่ถึงเวลารับรางวัล (11-12 เมษายน)'
        alert(msg)
      } finally {
        this.claiming = false
      }
    },

    goPlay () { router.push('/songkran-memory') },

    closeOrBack () {
      if (window.liff && window.liff.isInClient()) {
        window.liff.closeWindow()
      } else {
        router.push('/')
      }
    }
  }
}
</script>

<style scoped>
.songkran-ranking { min-height:100vh; background:linear-gradient(180deg,#0066cc 0%,#0099ff 50%,#33ccff 100%); padding-bottom:80px; }

/* Hero */
.ranking-hero { position:relative;overflow:hidden;margin:0;border-radius:0 0 24px 24px;border-bottom:2px solid rgba(255,215,0,0.3); }
.hero-bg { position:absolute;inset:0;background:linear-gradient(145deg,#1a0a3e,#0d1b4a 40%,#132e5b);opacity:0.95; }
.hero-content { position:relative;text-align:center;padding:28px 20px 24px; }
.hero-trophy { font-size:50px;filter:drop-shadow(0 4px 12px rgba(255,215,0,0.4));animation:float 3s ease-in-out infinite; }
@keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
.hero-title { font-size:26px;font-weight:900;letter-spacing:3px;background:linear-gradient(135deg,#FFD700,#FFA500,#FFD700);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;line-height:1.2;margin:6px 0 10px; }
.date-badge { display:inline-block;background:rgba(255,255,255,0.1);border:1px solid rgba(255,255,255,0.2);border-radius:20px;padding:4px 14px;font-size:12px;color:rgba(255,255,255,0.8);font-weight:600; }
.prize-row { display:flex;gap:10px;justify-content:center;margin-top:16px; }
.prize-card { flex:1;max-width:140px;padding:10px 8px;border-radius:14px;text-align:center;border:1.5px solid; }
.prize-gold { background:linear-gradient(145deg,rgba(255,215,0,0.12),rgba(255,180,0,0.06));border-color:rgba(255,215,0,0.35); }
.prize-silver { background:linear-gradient(145deg,rgba(180,200,220,0.12),rgba(140,160,180,0.06));border-color:rgba(180,200,220,0.3); }
.prize-icon { font-size:24px; }
.prize-label { font-size:10px;color:rgba(255,255,255,0.6);margin:2px 0;font-weight:600; }
.prize-amount { font-size:20px;font-weight:900;color:#FFD700; }
.prize-amount small { font-size:11px;color:rgba(255,255,255,0.6);font-weight:600; }
.btn-play-hero { margin-top:18px;font-size:18px;font-weight:800;padding:12px 40px;border:none;border-radius:50px;background:linear-gradient(135deg,#FFD700,#FF8C00);color:#1a1a1a;cursor:pointer;box-shadow:0 4px 20px rgba(255,140,0,0.4);animation:float 2s ease-in-out infinite; }
.claim-date-note { margin-top:14px;font-size:11px;color:rgba(255,200,100,0.8);font-weight:600; }

/* User info */
.my-info { display:flex;align-items:center;justify-content:center;gap:8px;padding:14px;background:rgba(0,10,40,0.5);margin:10px 12px;border-radius:14px;border:1.5px solid rgba(0,200,255,0.2); }
.my-avatar { width:32px;height:32px;border-radius:50%;border:2px solid rgba(255,255,255,0.3); }
.my-name { color:#fff;font-size:14px;font-weight:600; }
.my-rank-badge { background:rgba(255,215,0,0.2);border:1px solid rgba(255,215,0,0.4);border-radius:20px;padding:2px 10px;font-size:12px;color:#FFD700;font-weight:700; }

/* Claim */
.claim-banner { position:relative;margin:0 12px 10px;background:linear-gradient(135deg,#1a3a0a,#0a2a10);border:2px solid rgba(100,255,100,0.35);border-radius:18px;padding:14px 16px; }
.claimed-banner { background:linear-gradient(135deg,#0a2a20,#0a1a20);border-color:rgba(0,200,120,0.35); }
.claim-inner { display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap; }
.claim-text { color:#fff; }
.claim-title { font-size:15px;font-weight:800; }
.claim-desc { font-size:12px;color:rgba(255,255,255,0.7);margin-top:2px; }
.claim-desc strong { color:#FFD700; }
.btn-claim { font-size:14px;font-weight:800;padding:10px 24px;border:none;border-radius:50px;background:linear-gradient(135deg,#FFD700,#FF8C00);color:#1a1a1a;cursor:pointer;box-shadow:0 4px 15px rgba(255,140,0,0.4); }
.btn-claim:disabled { opacity:0.5;cursor:not-allowed; }

/* Panel */
.ranking-panel { margin:0 12px;background:linear-gradient(180deg,rgba(10,15,40,0.92),rgba(5,10,30,0.95));backdrop-filter:blur(12px);border-radius:24px;border:2px solid rgba(255,255,255,0.08);overflow:hidden; }
.panel-header { display:flex;align-items:center;justify-content:space-between;padding:14px 18px 10px;border-bottom:1px solid rgba(255,255,255,0.06); }
.panel-title { font-size:15px;font-weight:800;color:#fff; }
.panel-count { font-size:11px;background:rgba(255,255,255,0.08);padding:3px 10px;border-radius:10px;color:rgba(255,255,255,0.5);font-weight:600; }
.ranking-loading { text-align:center;color:rgba(255,255,255,0.6);padding:40px 20px;display:flex;flex-direction:column;align-items:center;gap:12px; }
.loading-spinner { width:28px;height:28px;border:3px solid rgba(255,255,255,0.1);border-top-color:#FFD700;border-radius:50%;animation:spin 0.8s linear infinite; }
@keyframes spin { to{transform:rotate(360deg)} }
.ranking-empty { text-align:center;padding:40px 20px; }
.empty-icon { font-size:40px;margin-bottom:10px; }
.empty-text { color:#fff;font-size:16px;font-weight:700; }
.empty-sub { color:rgba(255,255,255,0.5);font-size:12px;margin-top:4px; }

/* Podium */
.podium { display:flex;align-items:flex-end;justify-content:center;gap:6px;padding:16px 12px 0; }
.podium-item { flex:1;max-width:100px;text-align:center; }
.podium-crown { font-size:18px;animation:float 2s ease infinite; }
.podium-medal { font-size:26px; }
.podium-code { font-size:11px;color:#fff;font-weight:700;margin:3px 0 2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis; }
.podium-score { font-size:14px;font-weight:900;color:#FFD700;margin-bottom:6px; }
.podium-bar { border-radius:8px 8px 0 0;width:100%; }
.bar-1 { height:70px;background:linear-gradient(180deg,#FFD700,#c8960c);box-shadow:0 0 15px rgba(255,215,0,0.3); }
.bar-2 { height:50px;background:linear-gradient(180deg,#C0C0C0,#8a8a8a); }
.bar-3 { height:36px;background:linear-gradient(180deg,#CD7F32,#8a5520); }
.ranking-body { padding-bottom:10px; }

/* Items */
.ranking-item { display:flex;align-items:center;gap:8px;padding:10px 14px;margin:5px 8px;border-radius:10px;border:1.5px solid; }
.rank-top5 { background:linear-gradient(135deg,rgba(255,215,0,0.1),rgba(255,180,0,0.05));border-color:rgba(255,215,0,0.3);border-left:3px solid #FFD700; }
.rank-top10 { background:linear-gradient(135deg,rgba(160,200,240,0.08),rgba(120,160,200,0.04));border-color:rgba(160,200,240,0.2);border-left:3px solid #90b8e0; }
.rank-normal { background:rgba(255,255,255,0.02);border-color:rgba(255,255,255,0.04); }
.rank-me { border-color:rgba(0,200,255,0.5)!important;background:rgba(0,120,255,0.12)!important;border-left:3px solid #00c8ff!important; }
.rank-number { width:28px;text-align:center;font-size:14px;font-weight:900;color:rgba(255,255,255,0.35); }
.num-top5 { color:#FFD700;font-size:16px; }
.num-top10 { color:#90b8e0; }
.rank-info { flex:1;min-width:0; }
.rank-code { color:#fff;font-size:13px;font-weight:700; }
.rank-detail { color:rgba(255,255,255,0.4);font-size:9px;margin-top:1px; }
.rank-score { font-size:16px;font-weight:900;color:rgba(255,255,255,0.5);min-width:55px;text-align:right; }
.score-top5 { color:#FFD700;font-size:18px; }
.score-top10 { color:#90b8e0;font-size:17px; }
.rank-reward { min-width:40px;text-align:right; }
.reward-gold { font-size:11px;font-weight:800;color:#FFD700; }
.reward-silver { font-size:11px;font-weight:800;color:#90b8e0; }

/* Footer */
.ranking-footer { text-align:center;padding:20px 16px; }
.btn-back { font-size:14px;font-weight:700;padding:10px 30px;border:none;border-radius:50px;background:rgba(255,255,255,0.08);border:1.5px solid rgba(255,255,255,0.2);color:#fff;cursor:pointer; }
</style>
